/* iter55x — Starter novel-framework template picker.
 *
 * Shown from the WelcomeOverlay's "Start with a template" card AND
 * (later) reachable via File → Templates for existing projects that
 * still have empty chapters.
 *
 * Data comes from `/lib/starterTemplates.js`. Application logic lives
 * in `store.js:applyStarterTemplate`.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  BookOpen, Compass, Sparkles, RotateCw, Flame, Skull, Users, Loader2, Check, Info, Volume2, ThumbsUp, History, X as XIcon, Dices,
} from 'lucide-react';
import { STARTER_TEMPLATES } from '@/lib/starterTemplates';
import { usePW } from '@/lib/store';
import { businessApi, charactersApi } from '@/lib/api';
import { toast } from 'sonner';
import TemplateVariablesDialog from './TemplateVariablesDialog';

const ICONS = { BookOpen, Compass, Sparkles, RotateCw, Flame, Skull };

export default function StarterTemplatesDialog({ open, onOpenChange, onApplied }) {
  const { applyStarterTemplate, project } = usePW();
  const projectChapters = Number(project?.chapters_count || 0);
  const [selectedId, setSelectedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [pendingTpl, setPendingTpl] = useState(null);
  // iter55g51 — Voice preview state: {char, samples:[{text,model}], loading}
  const [voice, setVoice] = useState(null);
  // iter55g52 — Series Bible Auto-Link. Pre-fill map derived from the
  // current project's characters so a Sequel-to template (or any
  // multi-book template) inherits Book 1's names/ages.
  const [prefill, setPrefill] = useState(null);
  // iter55i — Cloud-synced voice prefs (per-user). Loaded once, mirrored
  // into localStorage for offline reads. Writes go to both.
  const [cloudPrefs, setCloudPrefs] = React.useState({});
  React.useEffect(() => {
    let alive = true;
    businessApi.voicePrefsGet()
      .then((r) => { if (alive) setCloudPrefs(r?.prefs || {}); })
      .catch(() => { /* anonymous / offline → keep {} */ });
    return () => { alive = false; };
  }, []);

  // iter55j — Voice Take Comparison. When non-null, `history` shows the
  // list of previous "Preferred" saves for a given voice_pref key.
  const [history, setHistory] = useState(null);
  // iter55m — Twist-per-beat rolls. Map chapter index → alt_beats[i+1]
  // (1-indexed; 0 = base beat). Rolled from the picker card.
  const [twistRolls, setTwistRolls] = useState({});
  const rollTwistsFor = (tpl) => {
    const rolls = {};
    (tpl.chapters || []).forEach((ch, i) => {
      const alts = ch.alt_beats || [];
      if (alts.length === 0) return;
      // 1..N inclusive — 0 means "keep base" (never rolled).
      rolls[i] = 1 + Math.floor(Math.random() * alts.length);
    });
    setTwistRolls(rolls);
    const twistedCount = Object.keys(rolls).length;
    if (twistedCount === 0) toast.info('No alt beats to roll on this template.');
    else toast.success(`Rolled ${twistedCount} twist${twistedCount === 1 ? '' : 's'}.`);
  };
  // iter55n — Re-roll a single chapter's twist without disturbing others.
  const rerollChapter = (tpl, chapterIdx) => {
    const ch = (tpl.chapters || [])[chapterIdx];
    const alts = ch?.alt_beats || [];
    if (alts.length === 0) return;
    setTwistRolls((prev) => ({
      ...prev,
      [chapterIdx]: 1 + Math.floor(Math.random() * alts.length),
    }));
  };
  const twistCount = (tpl) =>
    (tpl.chapters || []).reduce(
      (n, ch) => n + ((ch.alt_beats || []).length > 0 ? 1 : 0),
      0,
    );

  const savePref = React.useCallback(async (key, index, text) => {
    setCloudPrefs((p) => ({ ...p, [key]: { index, text } }));
    try { localStorage.setItem(key, String(index)); } catch { /* ignore */ }
    try { localStorage.setItem(`${key}_text`, text || ''); } catch { /* ignore */ }
    try { await businessApi.voicePrefsPut(key, index, text || ''); }
    catch { /* offline / anonymous — localStorage already synced */ }
  }, []);

  const previewVoice = async (tpl, character, mode = 'first') => {
    if (mode === 'first') setVoice({ char: character, samples: [], loading: true });
    else setVoice((v) => ({ ...(v || { char: character, samples: [] }), loading: true }));
    // iter55g56 / iter55i — Tone-Learned Voice. Prefer the cloud pref if
    // present; fall back to localStorage for older prefs that were
    // saved before we started syncing.
    let toneSeed = null;
    try {
      const key = `voice_pref_${tpl?.id || 'tpl'}_${(character?.name || '').replace(/\{\{|\}\}/g, '')}`;
      const cloud = cloudPrefs[key];
      if (cloud && cloud.text) {
        toneSeed = cloud.text;
      } else {
        const idx = localStorage.getItem(key);
        const savedText = localStorage.getItem(`${key}_text`);
        if (idx !== null && savedText) toneSeed = savedText;
      }
    } catch (_) { /* ignore */ }
    try {
      const r = await businessApi.voicePreview({
        character_name:        character.name,
        character_description: character.description,
        template_name:         tpl.name,
        genre_hint:            (tpl.tagline || '') + (mode === 'second' ? ' — an ALTERNATE take, contrast the tone from the first sample.' : ''),
        tone_seed:             toneSeed || undefined,
      });
      setVoice((v) => {
        const prior = (v && mode === 'second') ? (v.samples || []) : [];
        return {
          char: character,
          samples: [...prior, { text: r.preview || '(empty)', model: r.model || 'claude-sonnet-4-5' }],
          loading: false,
        };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Voice preview failed.');
      setVoice(null);
    }
  };

  const apply = async (tpl) => {
    setBusyId(tpl.id);
    try {
      // iter55m — If any chapter has alt_beats and the author rolled
      // twists (`twistRolls[chapterIndex]` > 0), swap in the chosen alt
      // as the new beat_note so the applied template renders the twist
      // variant, not the base beat.
      const materialised = { ...tpl };
      if (Array.isArray(tpl.chapters)) {
        materialised.chapters = tpl.chapters.map((ch, i) => {
          const roll = twistRolls?.[i];
          if (!roll || !Array.isArray(ch.alt_beats) || !ch.alt_beats[roll - 1]) return ch;
          return {
            ...ch,
            beat_note:      ch.alt_beats[roll - 1].beat,
            beat_note_alt:  true,
            beat_note_label: ch.alt_beats[roll - 1].label,
          };
        });
      }
      const ok = await applyStarterTemplate(materialised);
      if (ok) {
        setPendingTpl(null);
        onOpenChange?.(false);
        onApplied?.();
      }
    } finally {
      setBusyId(null);
    }
  };

  const handlePrimary = async () => {
    const tpl = STARTER_TEMPLATES.find((t) => t.id === selectedId);
    if (!tpl) return;
    if (Array.isArray(tpl.variables) && tpl.variables.length > 0) {
      // iter55g52 — Series Bible Auto-Link. Try to inherit values from
      // the current project's characters when the template exposes
      // variable keys we can map onto them.
      let inherited = null;
      try {
        if (project?.id) {
          const chars = await charactersApi.list(project.id);
          if (Array.isArray(chars) && chars.length > 0) {
            const varKeys = new Set(tpl.variables.map((v) => v.key));
            const map = {};
            const hero = chars[0], heroine = chars[1] || chars[0];
            if (varKeys.has('hero_name')     && hero?.name)     map.hero_name     = hero.name;
            if (varKeys.has('hero_age')      && hero?.age)      map.hero_age      = String(hero.age);
            if (varKeys.has('heroine_name')  && heroine?.name)  map.heroine_name  = heroine.name;
            if (varKeys.has('heroine_age')   && heroine?.age)   map.heroine_age   = String(heroine.age);
            if (Object.keys(map).length > 0) {
              inherited = map;
              // iter55g55 — Sequel Detection: if 2+ fields matched we
              // toast a clear sequel hint so the author knows Book 1's
              // bible is about to steer Book 2.
              if (Object.keys(map).length >= 2) {
                const names = [hero?.name, heroine?.name].filter(Boolean).join(' & ');
                toast.info(`Detected as a sequel to your existing project (${names}). Fields pre-filled.`);
              }
            }
          }
        }
      } catch (_e) { /* prefill is best-effort */ }
      setPrefill(inherited);
      setPendingTpl(tpl);
      return;
    }
    apply(tpl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="starter-templates-dialog">
        <DialogHeader>
          <DialogTitle className="text-[16px] inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Start with a template
          </DialogTitle>
          <DialogDescription>
            Pick a novel framework. We&apos;ll pre-fill your chapter outlines with
            each beat&apos;s guidance and add a few character stubs to your
            Bible. You can edit or delete anything afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 grid gap-3 md:grid-cols-2">
          {STARTER_TEMPLATES.map((tpl) => {
            const Icon = ICONS[tpl.icon] || Sparkles;
            const isSelected = selectedId === tpl.id;
            return (
              <button
                key={tpl.id}
                type="button"
                onClick={() => setSelectedId(tpl.id)}
                data-testid={`starter-template-${tpl.id}`}
                className={
                  'flex flex-col items-start rounded-md border p-3 text-left transition ' +
                  (isSelected
                    ? 'border-primary bg-primary/5 shadow-panel-active'
                    : 'border-border bg-card hover:border-primary/40')
                }
              >
                <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-sm bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-[13px] font-semibold">{tpl.name}</div>
                <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">
                  {tpl.tagline}
                </p>
                <div className="mt-2 flex items-center gap-3 text-[10.5px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <BookOpen className="h-3 w-3" /> {tpl.chapters.length} beats
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users className="h-3 w-3" /> {tpl.characters.length} character{tpl.characters.length === 1 ? '' : 's'}
                  </span>
                </div>
                {projectChapters > 0 && projectChapters < tpl.chapters.length && (
                  <div className="mt-2 flex items-start gap-1 rounded-sm bg-amber-500/10 px-1.5 py-1 text-[10px] leading-snug text-amber-700 dark:text-amber-400"
                       data-testid={`starter-grow-hint-${tpl.id}`}>
                    <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    <span>
                      Will add {tpl.chapters.length - projectChapters} chapter{tpl.chapters.length - projectChapters === 1 ? '' : 's'} to your project so no beats are lost.
                    </span>
                  </div>
                )}
                {tpl.characters && tpl.characters.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {tpl.characters.slice(0, 2).map((c, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); previewVoice(tpl, c); }}
                        data-testid={`starter-voice-${tpl.id}-${i}`}
                        className="inline-flex items-center gap-1 rounded-sm bg-secondary/60 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-muted-foreground hover:bg-primary/10 hover:text-primary"
                        title={`Hear ${c.name || 'this character'}'s voice`}
                      >
                        <Volume2 className="h-2.5 w-2.5" />
                        {(c.name || 'voice').replace(/\{\{|\}\}/g, '')}
                      </button>
                    ))}
                  </div>
                )}
                {/* iter55m — Twist-per-beat randomiser. */}
                {twistCount(tpl) > 0 && (
                  <div className="mt-2 w-full space-y-1">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSelectedId(tpl.id); rollTwistsFor(tpl); }}
                        data-testid={`starter-roll-twists-${tpl.id}`}
                        className="inline-flex items-center gap-1 rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] font-medium uppercase tracking-wider text-amber-700 hover:bg-amber-500/25 dark:text-amber-300"
                        title="Randomise the alt beats (dub-con ↔ slow-burn, etc.)"
                      >
                        <Dices className="h-2.5 w-2.5" /> Roll twists ({twistCount(tpl)})
                      </button>
                      {isSelected && Object.keys(twistRolls).length > 0 && (
                        <span className="text-[9.5px] text-amber-700 dark:text-amber-300"
                              data-testid={`starter-twist-count-${tpl.id}`}>
                          {Object.keys(twistRolls).length} rolled
                        </span>
                      )}
                    </div>
                    {/* iter55n — Per-chapter re-roll list, shown only
                        when this template is selected. */}
                    {isSelected && (
                      <ul className="max-h-32 space-y-0.5 overflow-y-auto rounded-sm bg-secondary/30 p-1"
                          data-testid={`starter-per-chapter-rolls-${tpl.id}`}>
                        {(tpl.chapters || []).map((ch, i) => {
                          if (!ch.alt_beats || ch.alt_beats.length === 0) return null;
                          const rolled = twistRolls[i];
                          const label = rolled ? ch.alt_beats[rolled - 1]?.label : 'Base beat';
                          return (
                            <li key={i} className="flex items-center gap-1 text-[9.5px]">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); rerollChapter(tpl, i); }}
                                data-testid={`starter-reroll-ch-${tpl.id}-${i}`}
                                title={`Re-roll Ch ${i + 1}`}
                                className="rounded-sm bg-amber-500/20 px-1 py-0 text-amber-700 hover:bg-amber-500/40 dark:text-amber-300"
                              >
                                <Dices className="h-2.5 w-2.5" />
                              </button>
                              <span className="font-mono-stat text-muted-foreground">Ch {i + 1}</span>
                              <span className="truncate text-foreground/80">{ch.title}</span>
                              <span className="ml-auto rounded-sm bg-amber-500/10 px-1 text-amber-700 dark:text-amber-300">
                                {label}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter className="mt-3 gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="starter-cancel">
            Cancel
          </Button>
          <Button
            onClick={handlePrimary}
            disabled={!selectedId || !!busyId}
            data-testid="starter-apply"
            className="gap-1.5"
          >
            {busyId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Apply template
          </Button>
        </DialogFooter>
      </DialogContent>

      <TemplateVariablesDialog
        open={!!pendingTpl}
        onOpenChange={(o) => { if (!o) { setPendingTpl(null); setPrefill(null); } }}
        template={pendingTpl}
        prefill={prefill}
        busy={!!busyId}
        onApply={(materialized) => apply(materialized)}
      />

      {/* iter55g51/g52 — Character Voice Preview + A/B compare modal. */}
      {voice && (
        <Dialog open={!!voice} onOpenChange={(o) => { if (!o) setVoice(null); }}>
          <DialogContent className="max-w-2xl" data-testid="voice-preview-dialog">
            <DialogHeader>
              <DialogTitle className="text-[15px] inline-flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Voice preview — {(voice.char?.name || '').replace(/\{\{|\}\}/g, '') || 'Character'}
              </DialogTitle>
              <DialogDescription>
                {voice.samples && voice.samples.length >= 2
                  ? 'A/B compare two takes on this character\u2019s voice. Pick the tone that lands for your reader.'
                  : 'A ~120-word first-person sample. Not applied to your project.'}
              </DialogDescription>
            </DialogHeader>

            <div className={
              'mt-2 grid gap-3 ' +
              ((voice.samples || []).length >= 2 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1')
            }>
              {(voice.samples || []).map((s, i) => {
                const key = `voice_pref_${selectedId || 'tpl'}_${(voice.char?.name || '').replace(/\{\{|\}\}/g, '')}`;
                // iter55i — Prefer the cloud pref; localStorage stays a
                // fallback for legacy saves.
                let saved = null;
                const cloud = cloudPrefs[key];
                if (cloud && typeof cloud.index === 'number') {
                  saved = String(cloud.index);
                } else {
                  try { saved = localStorage.getItem(key); } catch (_) { /* ignore */ }
                }
                const isPreferred = saved !== null && Number(saved) === i;
                return (
                  <div key={i}
                       className={
                         'relative min-h-[10rem] rounded-md border p-3 text-[12.5px] italic leading-relaxed transition ' +
                         (isPreferred ? 'border-primary bg-primary/5' : 'border-border bg-card')
                       }
                       data-testid={`voice-preview-sample-${i}`}>
                    <div className="mb-1.5 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider text-muted-foreground not-italic">
                      <span>Take {String.fromCharCode(65 + i)} · {s.model}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const r = await businessApi.voicePrefsHistory(key);
                              setHistory({ key, entries: r?.history || [] });
                            } catch { setHistory({ key, entries: [] }); }
                          }}
                          data-testid={`voice-preview-history-${i}`}
                          title="Previous takes you preferred for this character"
                          className="inline-flex items-center gap-1 rounded-sm bg-secondary/60 px-1.5 py-0.5 text-[9.5px] text-muted-foreground hover:bg-primary/10 hover:text-primary"
                        >
                          <History className="h-2.5 w-2.5" /> History
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            savePref(key, i, s.text || '');
                            toast.success(`Take ${String.fromCharCode(65 + i)} saved as your preferred voice.`);
                          }}
                          title="Prefer this take (syncs across devices)"
                          data-testid={`voice-preview-thumbs-${i}`}
                          className={
                            'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9.5px] transition ' +
                            (isPreferred
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary/60 text-muted-foreground hover:bg-primary/10 hover:text-primary')
                          }
                        >
                          <ThumbsUp className="h-2.5 w-2.5" />
                          {isPreferred ? 'Preferred' : 'Prefer'}
                        </button>
                      </div>
                    </div>
                    {s.text}
                  </div>
                );
              })}
              {voice.loading && (
                <div className="min-h-[10rem] flex items-center justify-center rounded-md border border-dashed border-border bg-card/50 p-3 text-[12px] text-muted-foreground">
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Writing…
                </div>
              )}
            </div>

            <DialogFooter className="mt-3 gap-2">
              {(voice.samples || []).length < 3 && !voice.loading && (voice.samples || []).length >= 1 && (
                <Button variant="outline"
                        onClick={() => {
                          const tpl = STARTER_TEMPLATES.find((t) => t.id === selectedId);
                          previewVoice(tpl || { name: '', tagline: '' }, voice.char, 'second');
                        }}
                        data-testid="voice-preview-compare"
                        className="gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" />
                  {(voice.samples || []).length === 1
                    ? 'Generate a second take (A/B)'
                    : 'Generate a third take (A/B/C)'}
                </Button>
              )}
              <Button variant="outline" onClick={() => setVoice(null)}
                      data-testid="voice-preview-close">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* iter55j — Voice Take Comparison. Modal listing every past
          preferred take for this character, ordered oldest→newest. */}
      {history && (
        <Dialog open={!!history} onOpenChange={(o) => { if (!o) setHistory(null); }}>
          <DialogContent className="max-w-2xl" data-testid="voice-history-dialog">
            <DialogHeader>
              <DialogTitle className="text-[15px] inline-flex items-center gap-2">
                <History className="h-4 w-4 text-primary" />
                Voice history
              </DialogTitle>
              <DialogDescription>
                Every take you\u2019ve marked as your preferred voice for this character. Hear how the tone has drifted book to book.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
              {(history.entries || []).length === 0 ? (
                <div className="rounded-md border border-border bg-secondary/20 p-6 text-center text-[12px] text-muted-foreground">
                  No previous takes saved yet. Preferred takes will appear here.
                </div>
              ) : (
                history.entries.slice().reverse().map((h, i) => (
                  <div key={i}
                       className="rounded-md border border-border bg-card p-3 text-[12.5px] italic leading-relaxed"
                       data-testid={`voice-history-entry-${i}`}>
                    <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground not-italic">
                      <span>Take {String.fromCharCode(65 + (h.index || 0))}</span>
                      <span className="font-mono-stat opacity-80">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
                    </div>
                    {h.text || '(empty)'}
                  </div>
                ))
              )}
            </div>
            <DialogFooter className="mt-3 gap-2">
              <Button variant="outline" onClick={() => setHistory(null)}
                      data-testid="voice-history-close" className="gap-1.5">
                <XIcon className="h-3.5 w-3.5" /> Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
